/**
 * Halftone randomizer for the closet wall.
 *
 * Ported from the team's refined feed mockup (closet-doors-clean.html). Every
 * cabinet on the feed gets its OWN halftone recipe — pattern, colour, strength,
 * dot size, spacing, softness and flow — drawn fresh on each page load. The
 * recipe is emitted as inline CSS custom properties set on the `.closet`
 * element; the `.ht-layer` overlay inside each door reads them (the composite
 * --ht / --ht-size / --ht-mask are fully resolved here so a per-cabinet draw
 * always wins over any defaults in Closet.css).
 *
 * Colours are the canonical brand accents (mirrors accent-presets.ts) plus Ink
 * for plain black-and-white dots.
 */
import type { CSSProperties } from 'react';
import { ACCENT_PRESETS } from './accent-presets';

/** Dot tints — Ink (B&W) + every on-brand accent hex. */
export const HALFTONE_COLORS: string[] = [
  '#14110F', // Ink
  ...ACCENT_PRESETS.map((p) => p.hex),
];

/**
 * Mask compositions that reshape the dot field. `even` = no mask (a flat,
 * uniform field). The rest carve the dots into blooms, vignettes, bands, etc.
 */
// Each carving mask keeps a LOW floor (~.16–.2) rather than fading fully to
// transparent: enough base so masked areas aren't stark white, but with strong
// contrast so each pattern reads as a clearly different design, not a subtle
// variation of the same dense field.
const PAT_MASKS: Record<string, string> = {
  even: 'none',
  bloom: 'radial-gradient(circle at 50% 40%, #000 6%, rgba(0,0,0,.85) 32%, rgba(0,0,0,.32) 74%, rgba(0,0,0,.18) 100%)',
  spotlight: 'radial-gradient(circle at 32% 26%, #000 3%, rgba(0,0,0,.8) 28%, rgba(0,0,0,.3) 66%, rgba(0,0,0,.16) 100%)',
  edge: 'radial-gradient(circle at 50% 50%, rgba(0,0,0,.18) 28%, rgba(0,0,0,.5) 62%, #000 100%)',
  rings: 'repeating-radial-gradient(circle at 50% 46%, #000 0 4.5px, rgba(0,0,0,.3) 4.5px 7px, rgba(0,0,0,.16) 7px 13px)',
  bands: 'repeating-linear-gradient(45deg, #000 0 6px, rgba(0,0,0,.3) 6px 10px, rgba(0,0,0,.16) 10px 19px)',
  cross: 'repeating-linear-gradient(45deg, #000 0 4.5px, rgba(0,0,0,.18) 4.5px 13px), repeating-linear-gradient(-45deg, #000 0 4.5px, rgba(0,0,0,.18) 4.5px 13px)',
  fade: 'linear-gradient(180deg, #000 0%, #000 20%, rgba(0,0,0,.2) 100%)',
  twin: 'radial-gradient(circle at 30% 30%, #000 2%, rgba(0,0,0,.8) 24%, rgba(0,0,0,.2) 58%), radial-gradient(circle at 72% 72%, #000 2%, rgba(0,0,0,.75) 24%, rgba(0,0,0,0) 58%)',
};
const PAT_KEYS = Object.keys(PAT_MASKS);

const rand = (a: number, b: number) => Math.random() * (b - a) + a;

/** Fisher–Yates shuffle (new array). */
function shuffled<T>(a: T[]): T[] {
  const r = a.slice();
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
}

/**
 * A no-repeat picker: hands out a DISTINCT item to each consecutive caller by
 * draining a shuffled queue, only reshuffling once every item has been used (and
 * never the same item straddling the reshuffle seam). Used so every door on a
 * load gets a unique pattern AND a unique colour, not just minor variations.
 */
function makeDistinctPicker<T>(items: T[]): () => T {
  let queue: T[] = [];
  let last: T | null = null;
  return () => {
    if (queue.length === 0) {
      queue = shuffled(items);
      if (queue.length > 1 && queue[queue.length - 1] === last) {
        const end = queue.length - 1;
        [queue[0], queue[end]] = [queue[end], queue[0]];
      }
    }
    last = queue.pop()!;
    return last;
  };
}

const nextMaskKey = makeDistinctPicker(PAT_KEYS);
const nextColor = makeDistinctPicker(HALFTONE_COLORS);

/**
 * Draw one random halftone recipe and return it as inline style props for a
 * `.closet` element. Dense field: tight dot spacing (2.6–4px) with larger dots
 * (0.7–1.5px) so the pattern fills the panel with minimal whitespace; strength
 * 0.5–0.7 so even the lightest draw clearly reads, softness 1.5–2.0, and ~45%
 * of cabinets get a slow drift.
 */
export function randomHalftoneStyle(): CSSProperties {
  const color = nextColor();
  const alpha = rand(0.5, 0.7);
  const dot = rand(0.7, 1.5);
  const gap = rand(2.6, 4);
  const soft = rand(1.5, 2.0);

  // Fully resolve the composite vars here so a per-cabinet draw beats the
  // :root-level defaults baked into Closet.css's var() fallbacks.
  const ink = `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
  const flow = Math.random() < 0.45 ? rand(1, 8) : 0;

  const style: Record<string, string> = {
    '--ht-color': color,
    '--ht-alpha': alpha.toFixed(2),
    '--ht-dot': `${dot.toFixed(2)}px`,
    '--ht-gap': `${gap.toFixed(2)}px`,
    '--ht-feather': `${soft.toFixed(2)}px`,
    '--ht': `radial-gradient(circle, ${ink} ${dot.toFixed(2)}px, transparent ${(dot + soft).toFixed(2)}px)`,
    '--ht-size': `${gap.toFixed(2)}px ${gap.toFixed(2)}px`,
    '--ht-mask': PAT_MASKS[nextMaskKey()],
    '--ht-play': flow ? 'running' : 'paused',
    '--ht-flow-dur': flow ? `${(44 / flow).toFixed(1)}s` : '30s',
  };
  return style as CSSProperties;
}
